import React from "react";
import {
  Box,
  Button,
  Select,
  Option,
  Typography,
  IconButton,
} from "@mui/joy";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const startItem = (currentPage - 1) * rowsPerPage + 1;
  const endItem = Math.min(currentPage * rowsPerPage, totalItems);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  if (totalPages <= 1) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 1,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography level="body-sm" color="neutral">
          Showing {totalItems} of {totalItems} items
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography level="body-sm">Rows per page:</Typography>
          <Select
            value={rowsPerPage}
            onChange={(_, value) => onRowsPerPageChange(value || 25)}
            size="sm"
            sx={{ minWidth: 80 }}
          >
            <Option value={25}>25</Option>
            <Option value={50}>50</Option>
            <Option value={100}>100</Option>
          </Select>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        px: 2,
        py: 1,
        borderTop: "1px solid",
        borderColor: "divider",
        gap: 2,
      }}
    >
      <Typography level="body-sm" color="neutral">
        Showing {startItem}-{endItem} of {totalItems} items
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography level="body-sm">Rows per page:</Typography>
          <Select
            value={rowsPerPage}
            onChange={(_, value) => onRowsPerPageChange(value || 25)}
            size="sm"
            sx={{ minWidth: 80 }}
          >
            <Option value={25}>25</Option>
            <Option value={50}>50</Option>
            <Option value={100}>100</Option>
          </Select>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            size="sm"
            variant="outlined"
            disabled={currentPage === 1}
            onClick={() => onPageChange(currentPage - 1)}
            sx={{ 
              width: 32, 
              height: 32,
              '&:disabled': {
                opacity: 0.5,
              }
            }}
          >
            <ChevronLeftIcon />
          </IconButton>

          {getPageNumbers().map((page, index) => (
            <React.Fragment key={index}>
              {page === "..." ? (
                <Typography level="body-sm" sx={{ px: 1, color: "text.secondary" }}>
                  ...
                </Typography>
              ) : (
                <Button
                  size="sm"
                  variant={currentPage === page ? "solid" : "outlined"}
                  onClick={() => onPageChange(page as number)}
                  sx={{ 
                    minWidth: 32, 
                    height: 32,
                    px: 1,
                    fontSize: "0.875rem",
                  }}
                >
                  {page}
                </Button>
              )}
            </React.Fragment>
          ))}

          <IconButton
            size="sm"
            variant="outlined"
            disabled={currentPage === totalPages}
            onClick={() => onPageChange(currentPage + 1)}
            sx={{ 
              width: 32, 
              height: 32,
              '&:disabled': {
                opacity: 0.5,
              }
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default Pagination; 